import type { Dayjs } from 'dayjs';
import type { RangeAvailabilityCalendarProps } from './types';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, CircularProgress } from '@mui/material';

import { RangeAvailabilityCalendarLegend } from './range-availability-calendar-legend';
import {
  CALENDAR_MONTH_GAP,
  CALENDAR_MONTH_MIN_HEIGHT,
  RangeAvailabilityCalendarMonth,
} from './range-availability-calendar-month';
import {
  getVisibleMonthRange,
  isDateSelectable,
  normalizeSelectedDates,
  toggleSelectedDate,
} from './utils';

const DEFAULT_MONTHS_TO_SHOW = 3;

const DEFAULT_LEGEND_LABELS = {
  window: 'Window',
  preferred: 'Preferred',
  excluded: 'Excluded',
} as const;

export const RangeAvailabilityCalendar = memo(
  ({
    window,
    preferred,
    excluded,
    selectedDates: selectedDatesProp,
    defaultSelectedDates = [],
    onSelectedDatesChange,
    visibleMonth: visibleMonthProp,
    defaultVisibleMonth,
    onVisibleMonthChange,
    onMonthRangeChange,
    monthsToShow = DEFAULT_MONTHS_TO_SHOW,
    legendLabels = DEFAULT_LEGEND_LABELS,
    loading = false,
    readOnly = false,
  }: RangeAvailabilityCalendarProps) => {
    const isSelectedControlled = selectedDatesProp !== undefined;
    const isVisibleMonthControlled = visibleMonthProp !== undefined;

    const [internalSelectedDates, setInternalSelectedDates] =
      useState<Dayjs[]>(defaultSelectedDates);
    const [internalVisibleMonth, setInternalVisibleMonth] = useState<Dayjs>(
      () => defaultVisibleMonth ?? window.from.startOf('month')
    );

    const selectedDates = isSelectedControlled ? selectedDatesProp : internalSelectedDates;
    const visibleMonth = isVisibleMonthControlled
      ? visibleMonthProp
      : internalVisibleMonth;

    const visibleMonths = useMemo(
      () =>
        Array.from({ length: monthsToShow }, (_, index) =>
          visibleMonth.add(index, 'month').startOf('month')
        ),
      [monthsToShow, visibleMonth]
    );

    const monthRange = useMemo(
      () => getVisibleMonthRange(visibleMonth, monthsToShow),
      [monthsToShow, visibleMonth]
    );

    useEffect(() => {
      onMonthRangeChange?.(monthRange);
    }, [monthRange, onMonthRangeChange]);

    const updateSelectedDates = useCallback(
      (nextSelectedDates: Dayjs[]) => {
        const normalizedDates = normalizeSelectedDates(nextSelectedDates);

        if (!isSelectedControlled) {
          setInternalSelectedDates(normalizedDates);
        }

        onSelectedDatesChange?.(normalizedDates);
      },
      [isSelectedControlled, onSelectedDatesChange]
    );

    const updateVisibleMonth = useCallback(
      (nextVisibleMonth: Dayjs) => {
        const normalizedMonth = nextVisibleMonth.startOf('month');

        if (!isVisibleMonthControlled) {
          setInternalVisibleMonth(normalizedMonth);
        }

        onVisibleMonthChange?.(normalizedMonth);
      },
      [isVisibleMonthControlled, onVisibleMonthChange]
    );

    const handleDateClick = useCallback(
      (date: Dayjs) => {
        if (readOnly || !isDateSelectable(date, window, excluded)) {
          return;
        }

        updateSelectedDates(toggleSelectedDate(selectedDates, date));
      },
      [excluded, readOnly, selectedDates, updateSelectedDates, window]
    );

    const handlePreviousMonth = useCallback(() => {
      updateVisibleMonth(visibleMonth.subtract(monthsToShow, 'month'));
    }, [monthsToShow, updateVisibleMonth, visibleMonth]);

    const handleNextMonth = useCallback(() => {
      updateVisibleMonth(visibleMonth.add(monthsToShow, 'month'));
    }, [monthsToShow, updateVisibleMonth, visibleMonth]);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <RangeAvailabilityCalendarLegend labels={legendLabels} />

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            width: 'fit-content',
            minWidth: '100%',
            mx: 'auto',
            gap: `${CALENDAR_MONTH_GAP}px`,
            minHeight: CALENDAR_MONTH_MIN_HEIGHT,
            py: 1,
          }}
        >
          {loading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                opacity: 0.72,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : null}

          {visibleMonths.map((month, index) => (
            <RangeAvailabilityCalendarMonth
              key={month.format('YYYY-MM')}
              month={month}
              window={window}
              preferred={preferred}
              excluded={excluded}
              selectedDates={selectedDates}
              onDateClick={handleDateClick}
              readOnly={readOnly}
              showPrevious={index === 0}
              showNext={index === visibleMonths.length - 1}
              onPrevious={handlePreviousMonth}
              onNext={handleNextMonth}
            />
          ))}
        </Box>
      </Box>
    );
  }
);

RangeAvailabilityCalendar.displayName = 'RangeAvailabilityCalendar';

export type {
  DateRange,
  DateRanges,
  RangeAvailabilityCalendarLegendLabels,
  RangeAvailabilityCalendarProps,
} from './types';

export {
  getRangeAvailabilityDayMeta,
  getVisibleMonthRange,
  isDateSelectable,
  isDateWithinRange,
  normalizeDateRanges,
  normalizeSelectedDates,
  toggleSelectedDate,
} from './utils';
